import React, { useState, useEffect } from 'react';
import { Edit3, Unlink } from 'lucide-react';
import apiService from '../../services/apiService';
import MarkdownText from '../ui/MarkdownText';
import { anhangArt } from '../../utils/anhangArt';
import { buildAggregatedHistory, buildAttachmentHistoryItems, darfKommentarBearbeiten, darfAnhaengeLoeschen } from '../../utils/ticketHistorie';
import MailAnhaenge from './MailAnhaenge';

// #530 (Scheibe 4 aus #310): die Historie in TicketDetail. Code unveraendert
// aus TicketDetail.js uebernommen, Muster aus #312/#471/#528.
//
// useTicketHistorie laedt die Historie (eigene oder aggregiert ueber Vorgang
// bzw. Teilvorgang, Anhaenge dort nachgeladen), zeigt sie in Portionen zu 30
// und springt auf Wunsch zu einem Eintrag. Wann geladen wird, entscheidet
// weiterhin der Init-Effekt in TicketDetail (nur bei neuer Ticket-id).
//
// erstelleHistorienEintrag bindet die Darstellung eines Eintrags an ihren
// Kontext, wie erstelleKalenderHelfer/erstelleTicketFormat. Achtung beim
// Erweitern: "history" ist auch ein Browser-Global (window.history). Fehlt der
// Name im Kontext, meldet ESLint nichts, und der Eintrag liest das falsche
// Objekt.
export function useTicketHistorie({ ticket, setError, scrollTo }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyListEl, setHistoryListEl] = useState(null);
  const HISTORY_INITIAL = 30;
  const HISTORY_STEP = 30;
  const [visibleHistoryCount, setVisibleHistoryCount] = useState(HISTORY_INITIAL);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  // Sichtbare Historien-Elemente zurücksetzen, wenn sich die Historie ändert
  useEffect(() => {
    try {
      setVisibleHistoryCount(Math.min(HISTORY_INITIAL, (history || []).filter(h => h.type !== 'system').length));
    } catch (_) {}
  }, [history]);

  // Infinite-Scroll: Mehr Elemente nachladen, wenn Scroll nahe Ende
  useEffect(() => {
    const el = historyListEl;
    if (!el) return;
    const onScroll = () => {
      try {
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300) {
          const total = (history || []).filter(h => h.type !== 'system').length;
          setVisibleHistoryCount(prev => Math.min(prev + HISTORY_STEP, total));
        }
      } catch (_) {}
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      try { el.removeEventListener('scroll', onScroll); } catch (_) {}
    };
  }, [history, historyListEl]);

  // Anhänge für Projekt/Unterprojekt nachladen und in Historie einsortieren (nicht blockierend)
  const loadAggregatedAttachmentsLazy = async (scopeType, scopeId) => {
    try {
      setAttachmentsLoading(true);
      let aRes = null;
      if (scopeType === 'subproject') {
        aRes = await apiService.getSubprojectAttachments(scopeId);
      } else if (scopeType === 'project') {
        aRes = await apiService.getProjectAttachments(scopeId);
      }
      const attachments = aRes?.attachments || [];
      const attachmentItems = buildAttachmentHistoryItems(attachments);
      setHistory(prev => {
        try {
          const withoutAttachment = (prev || []).filter(i => i.type !== 'attachment');
          const merged = [...withoutAttachment, ...attachmentItems];
          merged.sort((a, b) => new Date(b.sortTimestamp || b.timestamp) - new Date(a.sortTimestamp || a.timestamp));
          return merged;
        } catch (_) {
          return prev;
        }
      });
    } catch (aggErr) {
      console.warn('Aggregierte Anhänge konnten nicht nachgeladen werden:', aggErr?.message || aggErr);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const loadTicketHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Aggregierte Ansicht, wenn Ticket direkt zu Projekt/Unterprojekt gehört
      if (ticket.subprojectId) {
        try {
          const [hRes, eRes, tRes] = await Promise.all([
            apiService.getSubprojectHistory(ticket.subprojectId),
            apiService.getSubprojectEmails(ticket.subprojectId),
            // Teams-Nachrichten dürfen den Pfad nicht killen, falls der Endpoint
            // (noch) nicht existiert oder einen Fehler liefert.
            apiService.getSubprojectTeamsMessages(ticket.subprojectId).catch(err => {
              console.warn('Aggregierte Unterprojekt-Teams-Nachrichten nicht verfügbar:', err?.message || err);
              return { teamsMessages: [] };
            })
          ]);
          const aggregatedBase = buildAggregatedHistory(
            hRes?.history || [],
            eRes?.emails || [],
            [],
            tRes?.teamsMessages || []
          );
          setHistory(aggregatedBase);
          // Anhänge parallel nachladen (nicht blockierend)
          loadAggregatedAttachmentsLazy('subproject', ticket.subprojectId);
          return;
        } catch (aggErr) {
          console.warn('Aggregierte Unterprojekt-Historie nicht verfügbar, falle zurück auf Ticket-Historie:', aggErr?.message || aggErr);
          // Fallback to ticket-level history below
        }
      }
      if (ticket.projectId) {
        try {
          const [hRes, eRes, tRes] = await Promise.all([
            apiService.getProjectHistory(ticket.projectId),
            apiService.getProjectEmails(ticket.projectId),
            apiService.getProjectTeamsMessages(ticket.projectId).catch(err => {
              console.warn('Aggregierte Projekt-Teams-Nachrichten nicht verfügbar:', err?.message || err);
              return { teamsMessages: [] };
            })
          ]);
          const aggregatedBase = buildAggregatedHistory(
            hRes?.history || [],
            eRes?.emails || [],
            [],
            tRes?.teamsMessages || []
          );
          setHistory(aggregatedBase);
          // Anhänge parallel nachladen (nicht blockierend)
          loadAggregatedAttachmentsLazy('project', ticket.projectId);
          return;
        } catch (aggErr) {
          console.warn('Aggregierte Projekt-Historie nicht verfügbar, falle zurück auf Ticket-Historie:', aggErr?.message || aggErr);
          // Fallback to ticket-level history below
        }
      }

      // Fallback: Ticket-eigene Historie
      const response = await apiService.getTicketHistory(ticket.id);
      setHistory(response.history || []);
    } catch (error) {
      console.error('Error loading ticket history:', error);
      setError('Fehler beim Laden der Ticket-Historie');
    } finally {
      setLoading(false);
    }
  };

  // Scroll-to support: after history loads, find and scroll to target
  useEffect(() => {
    try {
      if (!scrollTo || loading) return;
      const doScroll = () => {
        try {
          const container = document.querySelector('.history-list');
          if (!container) return;

          // Prefer DOM-based targeting using data attributes to avoid index mismatches
          let targetEl = null;
          if (scrollTo.type === 'comment') {
            if (scrollTo.commentId) {
              targetEl = container.querySelector(`.history-item[data-history-type="comment"][data-comment-id="${scrollTo.commentId}"]`);
              if (!targetEl) {
                targetEl = container.querySelector(`.history-item[data-history-type="comment"][data-history-id="comment-${scrollTo.commentId}"]`);
              }
            }
            // Fallback: try to match by snippet within comment content
            if (!targetEl && scrollTo.snippet) {
              const needle = String(scrollTo.snippet).trim().toLowerCase();
              const nodes = Array.from(container.querySelectorAll('.history-item[data-history-type="comment"] .comment-content'));
              for (const n of nodes) {
                const txt = (n.textContent || '').toLowerCase();
                if (needle && txt.includes(needle)) { targetEl = n.closest('.history-item'); break; }
              }
            }
          } else if (scrollTo.type === 'email') {
            const wanted = (scrollTo.subject || '').toLowerCase();
            const nodes = Array.from(container.querySelectorAll('.history-item[data-history-type="email"]'));
            targetEl = nodes.find(el => (el.getAttribute('data-email-subject') || '').toLowerCase() === wanted) || null;
          }

          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (_) {}
      };
      const t = setTimeout(doScroll, 50);
      return () => clearTimeout(t);
    } catch (_) {}
  }, [scrollTo, loading, history]);

  return {
    history, setHistory, loading, historyListEl, setHistoryListEl, visibleHistoryCount, attachmentsLoading, loadTicketHistory
  };
}

export function erstelleHistorienEintrag({
  t, history, currentTicket, isProjectTicket, getCurrentUserEmail, formatDate, formatFileSize, cleanPreviewText, thumbnailUrls,
  handleOpenEditCommentModal, handleEmailClick, handleTeamsMessageClick, handleDetachEmail, handleImageClick, handleDownloadAttachment, handleDeleteAttachment,
  oeffneMailAnhang
}) {
  const renderHistoryItem = (item, index) => {
    // Regel: Nur neueste eigene Kommentare editier-/löschbar, wenn kein neuerer Fremd-Eintrag existiert
    const canEditItem = () => {
      if (item.type !== 'comment') return false;
      // In aggregierter Ansicht nur editieren, wenn die Ursprungs-Ticket-ID bekannt ist
      if (isProjectTicket && !item.ticketId) return false;
      // Regel in utils/ticketHistorie.js, die Mobilansicht benutzt sie auch.
      return darfKommentarBearbeiten(item, history, getCurrentUserEmail());
    };
    const isEmail = item.type === 'email';
    const isTeamsMessage = item.type === 'teams_message';
    const isSystem = item.type === 'system';
    const isAttachment = item.type === 'attachment';

    // Bestimme den Benutzernamen, der den Eintrag hinzugefügt hat
    const getUserName = () => {
      if (isSystem) {
        return t('ticketDetail.system', 'System');
      }
      if (isEmail || isTeamsMessage) {
        return item.addedBy || t('ticketDetail.unknown', 'Unbekannt');
      }
      if (isAttachment) {
        return item.uploadedByName || item.uploadedBy || t('ticketDetail.unknown', 'Unbekannt');
      }
      // Für Kommentare
      return item.authorName || t('ticketDetail.unknown', 'Unbekannt');
    };

    const getActionText = () => {
      if (isEmail) {
        return t('ticketDetail.emailAdded', 'E-Mail hinzugefügt');
      }
      if (isTeamsMessage) {
        return t('ticketDetail.teamsMessageAdded', 'Teams-Nachricht hinzugefügt');
      }
      if (isAttachment) {
        return t('ticketDetail.fileAdded', 'Datei hinzugefügt');
      }
      if (isSystem) {
        return '';
      }
      // Für normale Kommentare
      return t('ticketDetail.comment', 'Kommentar');
    };

    // Derive effective identifiers for data attributes
    const effectiveCommentId = (() => {
      if (item.type !== 'comment') return '';
      if (item.commentId) return String(item.commentId);
      try {
        const m = String(item.id || '').match(/^comment-(\d+)/);
        return m ? m[1] : '';
      } catch (_) { return ''; }
    })();

    return (
      <div key={index} className="history-item" data-history-type={item.type} data-history-id={item.id || ''} data-comment-id={effectiveCommentId} data-email-subject={item.email?.subject || ''}>
        <div className="history-header">
          <div className="history-user-action">
            <span className="history-user">
              {getUserName()}
            </span>
            {(() => {
              const action = getActionText();
              const isOwnComment = item.type === 'comment' && (getCurrentUserEmail()?.toLowerCase() === (item.authorEmail || '').toLowerCase());
              return action ? (
                <span className="history-action" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {action}
                  {isOwnComment && canEditItem() && (
                    <button 
                      type="button"
                      className="edit-comment-btn"
                      title={t('ticketDetail.edit', 'Bearbeiten')} aria-label={t('ticketDetail.edit', 'Bearbeiten')}
                      onClick={() => handleOpenEditCommentModal(item)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      <Edit3 size={14} />
                    </button>
                  )}
                  {isEmail && item.loesbar && item.email && (
                    <button
                      type="button"
                      className="detach-email-btn"
                      title={t('ticketDetail.detachEmailTitle', 'Mail vom Ticket lösen')} aria-label={t('ticketDetail.detachEmailTitle', 'Mail vom Ticket lösen')}
                      onClick={() => handleDetachEmail(item)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      <Unlink size={14} />
                    </button>
                  )}
                </span>
              ) : null;
            })()}
          </div>
          <div className="history-timestamp">
            {formatDate(item.timestamp)}
          </div>
        </div>
        
        <div className="history-content">
          {isEmail && item.email ? (
            <div 
              className="email-content"
              onClick={() => handleEmailClick(item.email)}
              style={{ cursor: 'pointer' }}
            >
              <div className="email-header">
                <div className="email-direction-indicator">
                  {item.emailDirection === 'outgoing' ? (
                    <span className="direction-arrow outgoing" title={t('ticketDetail.outgoingEmail', 'Ausgehende E-Mail')}>
                      ⬆
                    </span>
                  ) : item.emailDirection === 'incoming' ? (
                    <span className="direction-arrow incoming" title={t('ticketDetail.incomingEmail', 'Eingehende E-Mail')}>
                      ⬇
                    </span>
                  ) : (
                    // #232: Drei-Zustands-Modell — fehlende Richtung nicht als eingehend maskieren
                    <span className="direction-arrow unknown" title={t('ticketDetail.unknownEmailDirection', 'Richtung unbekannt')}>
                      ?
                    </span>
                  )}
                </div>
                <strong>{t('ticketDetail.subject', 'Betreff')}:</strong> {item.email.subject}
              </div>
              <div className="email-meta">
                <span><strong>{t('ticketDetail.from', 'Von')}:</strong> {item.email.fromName || item.email.fromAddress}</span>
                <span><strong>{t('ticketDetail.to', 'An')}:</strong> {item.email.toAddresses}</span>
                <span><strong>{t('ticketDetail.received', 'Empfangen')}:</strong> {formatDate(item.email.receivedDateTime)}</span>
              </div>
              {item.email.bodyPreview && (
                <div className="email-preview">
                  {cleanPreviewText(item.email.bodyPreview)}
                </div>
              )}
              <MailAnhaenge anhaenge={item.mailAnhaenge} onOeffnen={oeffneMailAnhang ? oeffneMailAnhang(item.mailAnhaenge) : undefined} />
            </div>
          ) : isTeamsMessage && item.teamsMessage ? (
            <div 
              className="teams-message-content"
              onClick={() => handleTeamsMessageClick(item.teamsMessage)}
              style={{ cursor: 'pointer' }}
            >
              <div className="teams-message-header">
                <strong>{t('ticketDetail.teamsMessage', 'Teams-Nachricht')}:</strong> {item.teamsMessage.subject || t('ticketDetail.comment', 'Nachricht')}
              </div>
              <div className="teams-message-meta">
                <span><strong>{t('ticketDetail.from', 'Von')}:</strong> {item.teamsMessage.fromName}</span>
                <span><strong>{t('ticketDetail.chat', 'Chat')}:</strong> {item.teamsMessage.chatTopic || t('ticketDetail.directMessage', 'Direktnachricht')}</span>
                <span><strong>{t('ticketDetail.sent', 'Gesendet')}:</strong> {formatDate(item.teamsMessage.createdDateTime)}</span>
              </div>
              {item.teamsMessage.plainTextBody && (
                <div className="teams-message-preview">
                  {cleanPreviewText(item.teamsMessage.plainTextBody)}
                </div>
              )}
            </div>
          ) : isAttachment && (item.attachment || item.attachments) ? (
            <div className="attachment-content">
              {(() => {
                // Handle both single attachment (legacy) and multiple attachments (new)
                const attachments = item.attachments || [item.attachment];
                const totalSize = attachments.reduce((sum, att) => sum + att.size, 0);
                const nonImageAttachments = attachments.filter(att => !att.isImage);
                
                const canDeleteFromThisGroup = darfAnhaengeLoeschen(item, history, getCurrentUserEmail());
                
                return (
                  <>
                    <div className="attachment-header">
                      {attachments.length === 1 ? (
                        attachments[0].isImage ? (
                          <><strong>{t('ticketDetail.file', 'Datei')}</strong></>
                        ) : (
                          <><strong>{t('ticketDetail.file', 'Datei')}:</strong> {attachments[0].originalName}</>
                        )
                      ) : (
                        nonImageAttachments.length > 0 ? (
                          <><strong>{attachments.length} {t('ticketDetail.files', 'Dateien')}:</strong> {nonImageAttachments.map(att => att.originalName).join(', ')}</>
                        ) : (
                          <><strong>{attachments.length} {t('ticketDetail.files', 'Dateien')}</strong></>
                        )
                      )}
                    </div>
                    <div className="attachment-meta">
                      <span><strong>{t('ticketDetail.totalSize', 'Gesamtgröße:')}</strong> {formatFileSize(totalSize)}</span>
                      {attachments.length === 1 && (
                        <span><strong>Typ:</strong> {attachments[0].contentType}</span>
                      )}
                      {attachments[0].description && (
                        <span><strong>Beschreibung:</strong> {attachments[0].description}</span>
                      )}
                    </div>
                    <div className="attachments-grid">
                      {attachments.map((attachment, attIndex) => (
                        <div key={attachment.id} className="attachment-item">
                          {canDeleteFromThisGroup && (!isProjectTicket || attachment.ticketId) && (
                            <button
                              className="attachment-delete-x"
                              title={t('ticketDetail.deleteAttachment', 'Anhang löschen')}
                              onClick={() => handleDeleteAttachment(attachment.ticketId || currentTicket.id, attachment.id, attachment.originalName)}
                            >
                              ×
                            </button>
                          )}
                          {attachment.isImage ? (
                            <div className="attachment-images">
                          <div 
                                className="attachment-thumbnail"
                                data-attachment-id={attachment.id}
                                data-ticket-id={attachment.ticketId || currentTicket.id}
                                onClick={() => handleImageClick(attachment.ticketId || currentTicket.id, attachment.id, attachment.originalName)}
                                style={{ cursor: 'pointer' }}
                              >
                                {thumbnailUrls[attachment.id] ? (
                                  <img 
                                    src={thumbnailUrls[attachment.id]}
                                    alt={attachment.originalName}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                ) : (
                                  <div className="thumbnail-loading" title={t('ticketDetail.loadingPreview', 'Lade Vorschau...')} aria-label={t('ticketDetail.loadingPreview', 'Lade Vorschau...')}>
                                    <span className="dot"></span>
                                    <span className="dot"></span>
                                    <span className="dot"></span>
                                  </div>
                                )}
                                <div className="thumbnail-fallback" style={{ display: 'none' }}>
                                  📷 {attachment.originalName}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="attachment-file">
                              <div className="file-icon">📄</div>
                              <div className="file-info">
                                <div className="file-name">{attachment.originalName}</div>
                                <div className="file-size">{formatFileSize(attachment.size)}</div>
                                {anhangArt(attachment) === 'pdf' && (
                                  <button
                                    className="download-btn"
                                    onClick={() => handleImageClick(attachment.ticketId || currentTicket.id, attachment.id, attachment.originalName)}
                                  >
                                    👁️ {t('ticketDetail.view', 'Ansehen')}
                                  </button>
                                )}
                                <button 
                                  className="download-btn"
                                  onClick={() => handleDownloadAttachment(attachment.ticketId || currentTicket.id, attachment.id, attachment.originalName)}
                                >
                                  ⬇️ {t('ticketDetail.download', 'Herunterladen')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : isSystem ? (
            <div className="system-comment">
              {item.content}
            </div>
          ) : (
            <div className="comment-content">
              {/* #231a: Markdown in Kommentaren (konservative Variante: keine Überschriften/Bilder,
                  einfache Umbrüche bleiben) — KI-Kommentare, Fettdruck und Listen werden lesbar */}
              <MarkdownText variant="comment">{item.content}</MarkdownText>
            </div>
          )}
        </div>
      </div>
    );
  };
  return renderHistoryItem;
}
